import { defineStore } from 'pinia';

export const useToastStore = defineStore('toast', {
  state: () => ({
    message: '',
    color: '',
    show: false,
    timeout: 3000,
    isVisible: false,
    title: '',
    titleParams: {},
    text: '',
    item: '',
    num: '',
    action: '',
    entityName: '',
    from: '',
    to: '',
    icon: '',
    confirmButtonText: '',
    denyButtonText: '',
    closeButtonText: '',
    closeButtonColor: 'grey4',
    showConfirmButton: false,
    showDenyButton: false,
    showCloseButton: false,
    onConfirm: null,
    onDeny: null,
    onClose: null,
    timerProgressBar: false,
    duration: 5,
    type: 'success',
    isLoading: false,
    customContent: null,
    customContentProps: null,
    customContentInstance: null,
    footerClass: '',
  }),

  getters: {
    background: (state) => {
      switch (state.type) {
        case 'success':
          return '#EFFBF5';
        case 'error':
        case 'delete':
        case 'alert':
          return '#FFF7F5';
        case 'info':
          return '#F1FBFE';
        case 'warning':
          return '#FFFBF5';
        default:
          return 'grey lighten-1';
      }
    },

    modalIcon: (state) => {
      if (state.icon) return state.icon;
      switch (state.type) {
        case 'success':
          return 'solar:check-circle-bold';
        case 'error':
          return 'solar:close-circle-bold';
        case 'delete':
          return 'solar:trash-bin-trash-bold';
        case 'info':
          return 'solar:danger-circle-bold';
        case 'alert':
          return 'solar:bell-bing-bold';
        case 'warning':
          return 'solar:info-circle-linear';
        default:
          return 'solar:minus-circle-bold';
      }
    },

    iconColor: (state) => {
      switch (state.type) {
        case 'success':
          return '#2CAA69';
        case 'error':
        case 'delete':
        case 'alert':
          return '#F84C20';
        case 'info':
          return '#16AACF';
        case 'warning':
          return '#F49820';
        default:
          return 'grey';
      }
    },

    iconTheme: (state) => {
      switch (state.type) {
        case 'success':
          return 'radial-gradient(ellipse at center, #A6E8C6 30%, #86DFB1 31%, #A6E8C6 32%, #A6E8C6 37%, #86DFB1 38%, #C2EFD8 39%, #C2EFD8 44%, #E3F8ED 45%, #E3F8ED 50%, #C2EFD8 51%, #C2EFD8 51%, #EFFBF5 52%, #EFFBF5 100%)';
        case 'error':
        case 'delete':
        case 'alert':
          return 'radial-gradient(ellipse at center, #FDCBBF 30%, #FCB7A6 31%, #FDCBBF 32%, #FDCBBF 37%, #FCB7A6 38%, #FEDBD2 39%, #FEDBD2 44%, #FEEFEB 45%, #FEEFEB 50%, #FEDBD2 51%, #FEDBD2 51%, #FFF7F5 52%, #FFF7F5 100%)';
        case 'info':
          return 'radial-gradient(ellipse at center, #A3E5F5 30%, #83DBF2 31%, #A3E5F5 32%, #A3E5F5 37%, #83DBF2 38%, #C3EEF9 39%, #C3EEF9 44%, #DFF6FC 45%, #DFF6FC 50%, #C3EEF9 51%, #C3EEF9 51%, #F1FBFE 52%, #F1FBFE 100%)';
        case 'warning':
          return 'radial-gradient(ellipse at center, #FCE0BB 30%, #FAD4A3 31%, #FCE0BB 32%, #FCE0BB 37%, #FAD4A3 38%, #FDEBD3 39%, #FDEBD3 44%, #FEF4E7 45%, #FEF4E7 50%, #FDEBD3 51%, #FDEBD3 51%, #FFFBF5 52%, #FFF7F5 100%)';
        default:
          return 'grey';
      }
    },

    confirmBtnTheme: (state) => {
      switch (state.type) {
        case 'success':
          return '#2CAA69';
        case 'error':
        case 'delete':
        case 'alert':
          return '#F84C20';
        case 'info':
          return '#16AACF';
        case 'warning':
          return '#F49820';
        default:
          return 'grey';
      }
    },
    customContentInstanceComputed(): any {
      return this.customContentInstance;
    },
  },

  actions: {
    setCustomComponentInstance(instance: any) {
      this.customContentInstance = instance;
    },
    showToast(
      message: string,
      color: string = 'success',
      timeout: number = 3000
    ) {
      this.message = message;
      this.color = color;
      this.timeout = timeout;
      this.show = true;
    },
    hideToast() {
      this.show = false;
    },

    showAlert(options: any) {
      setTimeout(() => {
        this.title = options.title || '';
        this.titleParams = options.titleParams || {};
        this.text = options.text || '';
        this.item = options.item || '';
        this.num = options.num || '';
        this.action = options.action || '';
        this.entityName = options.entityName || '';
        this.from = options.from || '';
        this.to = options.to || '';
        this.icon = options.icon || '';
        this.confirmButtonText = options.confirmButtonText || 'newConfirmText';
        this.denyButtonText = options.denyButtonText || 'newCloseText';
        this.closeButtonText = options.closeButtonText || 'close';
        this.closeButtonColor = options.closeButtonColor || 'grey4';
        this.showConfirmButton = options.showConfirmButton || false;
        this.showDenyButton = options.showDenyButton || false;
        this.showCloseButton = options.showCloseButton || false;
        this.onConfirm = options.onConfirm;
        this.onDeny = options.onDeny;
        this.onClose = options.onClose;
        this.timerProgressBar = options.timerProgressBar || false;
        this.type = options.type || 'success';
        this.duration = options.duration || this.type === 'success' ? 3 : 7;
        this.customContent = options.customContent || null;
        this.customContentProps = options.customContentProps || null;
        this.isVisible = true;
        this.footerClass = options.footerClass || '';
      }, 400);
    },

    async confirm() {
      this.isLoading = true;
      let res = true;
      if (this.onConfirm) {
        res = await this.onConfirm(this);
        // some confirm functions are not returning value so they should pass and close and
        // deny close function only when they return false
        if (res === undefined) res = true;
      }
      if (res) this.isVisible = false;
      this.isLoading = false;
    },
    deny() {
      if (this.onDeny) this.onDeny();
      this.isVisible = false;
      this.isLoading = false;
    },
    close() {
      if (this.onClose) this.onClose();
      this.isVisible = false;
      this.isLoading = false;
    },
  },
});
